import { useEffect, useState } from 'react';
import { supChatBotLeadActivityFun } from '../../../api/supportAdmin';
import styles from './WebsiteTab.module.css';

// What a chat lead did on the website (from the bot's personal ?ref= links)

const STEPS = [
    { key: 'firstVisitAt', label: 'Opened link' },
    { key: 'tripViews', label: 'Viewed trip' },
    { key: 'bookingStartedAt', label: 'Picked a date' },
    { key: 'checkoutAt', label: 'Opened checkout' },
    { key: 'paidAt', label: 'Paid' }
];

// tripQuestionTopics -> label
const TOPIC_LABELS = {
    price: 'Price',
    reserve: 'Reserve / advance',
    dates: 'Dates',
    pickup: 'Pickup',
    timing: 'Timings',
    carry: 'What to carry',
    food: 'Food',
    stay: 'Stay',
    included: "What's included",
    difficulty: 'Difficulty',
    itinerary: 'Itinerary',
    safety: 'Safety',
    cancel: 'Cancellation',
    faq: 'Trip FAQ',
    overview: 'Opened trip details'
};

const scoreLabel = (score) => {
    if (score >= 100) return { text: 'Booked', tone: 'booked' };
    if (score >= 70) return { text: 'Very likely to book', tone: 'hot' };
    if (score >= 40) return { text: 'Interested', tone: 'warm' };
    if (score >= 15) return { text: 'Just looking', tone: 'cool' };
    return { text: 'Low intent', tone: 'cold' };
};

const timeAgo = (d) => {
    if (!d) return '';
    const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} h ago`;
    return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const shortDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }) : '';

export default function WebsiteTab({ user }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Reload when the lead's website activity or score changes (live socket updates)
    const reloadKey = `${user?._id}|${user?.leadScore}|${user?.web?.lastVisitAt || ''}|${user?.interestLevel}`;

    useEffect(() => {
        if (!user?._id) return;
        let cancelled = false;
        setError(false);
        supChatBotLeadActivityFun(user._id)
            .then((res) => !cancelled && setData(res?.data?.data || null))
            .catch(() => !cancelled && setError(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadKey]);

    if (loading) return <div className={styles.empty}>Loading website activity…</div>;
    if (error) return <div className={styles.empty}>Couldn't load website activity. Try again in a moment.</div>;
    if (!data) return null;

    const { score, reasons = [], web = {}, timeline = [], questions } = data;
    const label = scoreLabel(score);
    const hasVisited = Boolean(web.firstVisitAt);

    return (
        <div className={styles.wrap}>
            {/* Score */}
            <div className={`${styles.scoreCard} ${styles[label.tone]}`}>
                <div className={styles.scoreNum}>
                    {score}
                    <small>/100</small>
                </div>
                <div className={styles.scoreText}>
                    <b>{label.text}</b>
                    <div className={styles.bar}>
                        <span style={{ width: `${Math.min(score, 100)}%` }} />
                    </div>
                </div>
            </div>

            {reasons.length > 0 && (
                <div className={styles.reasons}>
                    {reasons.map((r) => (
                        <span key={r} className={r.startsWith('Quiet') ? styles.reasonMinus : styles.reason}>
                            {r}
                        </span>
                    ))}
                </div>
            )}

            {/* Questions the bot answered */}
            {questions?.count > 0 && (
                <>
                    <div className={styles.sectionTitle}>Asked the bot ({questions.count})</div>
                    <div className={styles.reasons}>
                        {questions.topics.map((t) => (
                            <span key={t} className={styles.reason}>
                                {TOPIC_LABELS[t] || t}
                            </span>
                        ))}
                    </div>
                    {questions.last && (
                        <div className={`${styles.lastQuestion} ${questions.last.answered ? '' : styles.unanswered}`}>
                            <span>“{questions.last.text}”</span>
                            <small>
                                {questions.last.answered ? 'Bot answered' : 'Bot had no answer — please reply'} ·{' '}
                                {timeAgo(questions.last.at)}
                            </small>
                        </div>
                    )}
                </>
            )}

            {/* Funnel */}
            <div className={styles.sectionTitle}>On the website</div>
            {!hasVisited ? (
                <div className={styles.empty}>
                    Hasn't opened a trip link yet. Every link the bot sends is personal — activity shows here as soon as
                    they open one.
                </div>
            ) : (
                <div className={styles.steps}>
                    {STEPS.map((s) => {
                        const value = web[s.key];
                        const done = s.key === 'tripViews' ? value > 0 : Boolean(value);
                        const notPaid = s.key === 'checkoutAt' && done && web.checkoutPaid === false;
                        return (
                            <div
                                key={s.key}
                                className={`${styles.step} ${done ? styles.done : ''} ${notPaid ? styles.warn : ''}`}
                            >
                                <i>{done ? (notPaid ? '!' : '✓') : ''}</i>
                                <span>
                                    {s.label}
                                    {s.key === 'tripViews' && value > 1 ? ` ×${value}` : ''}
                                </span>
                                {notPaid && <em>not paid</em>}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Timeline */}
            {timeline.length > 0 && (
                <>
                    <div className={styles.sectionTitle}>Activity</div>
                    <ul className={styles.timeline}>
                        {timeline.map((t, i) => (
                            <li
                                key={`${t.at}-${i}`}
                                className={
                                    t.event === 'payment_failed' || t.event === 'checkout_started'
                                        ? styles.important
                                        : t.event === 'payment_success'
                                          ? styles.good
                                          : ''
                                }
                            >
                                <div className={styles.tlMain}>
                                    <b>{t.label}</b>
                                    {t.tripTitle && <span> · {t.tripTitle}</span>}
                                    {(t.departure || t.people) && (
                                        <span className={styles.tlMeta}>
                                            {[shortDate(t.departure), t.people ? `${t.people} people` : '']
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </span>
                                    )}
                                </div>
                                <time>{timeAgo(t.at)}</time>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
